import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface LoadingScreenState {
    visible: boolean;
    messageKey?: string;
}

const SHOW_DELAY_MS = 400;
// Must match the opacity transition duration in loading-screen.component.scss.
const FADE_DURATION_MS = 200;
const MIN_VISIBLE_DURATION_MS = 1000;

@Injectable({ providedIn: 'root' })
export class LoadingScreenService {

    loadingScreenEventEmitter = new Subject<LoadingScreenState>();

    private showDelayTimerId?: ReturnType<typeof setTimeout>;
    private hideDelayTimerId?: ReturnType<typeof setTimeout>;
    private shownAt?: number;

    show(messageKey?: string): void {
        // A show() always wins over a pending hide.
        clearTimeout(this.hideDelayTimerId);
        this.hideDelayTimerId = undefined;

        if (this.shownAt !== undefined) {
            // Already visible: swap the message in place, don't restart the delay.
            this.loadingScreenEventEmitter.next({ visible: true, messageKey });
            return;
        }

        if (this.showDelayTimerId !== undefined) {
            // Show delay already pending — avoid stacking timers.
            return;
        }

        // Debounce: don't actually reveal the screen until SHOW_DELAY_MS elapses, so
        // operations faster than the delay never cause a flicker.
        this.showDelayTimerId = setTimeout(() => {
            this.showDelayTimerId = undefined;
            this.shownAt = Date.now();
            this.loadingScreenEventEmitter.next({ visible: true, messageKey });
        }, SHOW_DELAY_MS);
    }

    hide(): void {
        if (this.showDelayTimerId !== undefined) {
            // Screen never became visible yet — just cancel the pending show, nothing to emit.
            clearTimeout(this.showDelayTimerId);
            this.showDelayTimerId = undefined;
            return;
        }

        if (this.shownAt === undefined) {
            // Already hidden.
            return;
        }

        const elapsed = Date.now() - this.shownAt;

        if (elapsed < FADE_DURATION_MS) {
            // Still within the CSS fade-in — hide immediately, the fade masks the abrupt disappearance.
            this.emitHide();
            return;
        }

        if (this.hideDelayTimerId !== undefined) {
            // Hide delay already pending.
            return;
        }

        // Enforce a minimum visible duration so the screen doesn't flash briefly.
        const remaining = MIN_VISIBLE_DURATION_MS - elapsed;

        if (remaining <= 0) {
            this.emitHide();
        } else {
            this.hideDelayTimerId = setTimeout(() => this.emitHide(), remaining);
        }
    }

    private emitHide(): void {
        this.hideDelayTimerId = undefined;
        this.shownAt = undefined;
        this.loadingScreenEventEmitter.next({ visible: false });
    }
}
